import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductSearchResponse } from '@/features/self-service/api/search';
import {
  adaptSearchGroups,
  type SearchResultModel,
} from '@/features/self-service/utils/search-results';
import { storefrontKeys } from '../api/keys';
import {
  searchRetry,
  searchRetryDelay,
  searchWithScopedKey,
  StorefrontSearchUnavailable,
} from '../api/scoped-search';

/**
 * Sugestões visíveis de uma vez.
 *
 * Seis porque o painel não tem scroll: uma lista que rola dentro de um menu
 * suspenso disputa a roda do mouse com a página atrás dele. Quem quer ver mais
 * aperta Enter e cai na listagem, que pagina de verdade.
 */
export const SUGGESTION_LIMIT = 6;

/**
 * Abaixo disto não se busca.
 *
 * Uma letra casa quase todo o índice, então a lista chegaria cheia e sem
 * informação — e ainda pagaria uma ida ao Typesense a cada tecla do começo da
 * palavra, que é justamente quando a pessoa digita mais rápido.
 */
export const MIN_TERM_LENGTH = 2;

export interface StorefrontAutocompleteResult {
  suggestions: SearchResultModel[];
  /** Contagem de **modelos**, para o rodapé "ver todos os N". */
  found: number;
  isFetching: boolean;
  /** Busca desligada por termo curto — não é erro nem lista vazia. */
  isIdle: boolean;
  /** Conta sem chave: some com o campo em vez de mostrá-lo quebrado. */
  isUnavailable: boolean;
}

/**
 * As sugestões do campo do topo.
 *
 * Roda contra o MESMO caminho escopado da listagem (`searchWithScopedKey`), e
 * não contra um endpoint próprio: o recorte de marcas do buyer vive dentro da
 * chave do Typesense, então qualquer atalho aqui devolveria peça de marca que a
 * conta não pode ver.
 *
 * Recebe o termo **já debounced** — o componente é dono da cadência porque é
 * ele que sabe quando o campo está aberto.
 */
export function useStorefrontAutocomplete(
  term: string
): StorefrontAutocompleteResult {
  const queryClient = useQueryClient();
  const q = term.trim();
  const enabled = q.length >= MIN_TERM_LENGTH;

  const query = useQuery<ProductSearchResponse, Error>({
    queryKey: storefrontKeys.autocomplete(q),
    queryFn: ({ signal }) =>
      searchWithScopedKey(queryClient, {
        q,
        page: 1,
        perPage: SUGGESTION_LIMIT,
        // Sem `sortBy`: com termo, a relevância do Typesense é exatamente o que
        // uma sugestão deve ordenar. E sem `facetBy`, que aqui só custaria
        // tempo de resposta para um painel que não desenha filtro.
        signal,
      }),
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    // Digitar a letra seguinte não deve esvaziar o painel: a lista anterior
    // fica até a nova chegar.
    placeholderData: keepPreviousData,
    retry: searchRetry,
    retryDelay: searchRetryDelay,
  });

  return {
    suggestions: enabled ? adaptSearchGroups(query.data?.grouped_hits ?? []) : [],
    found: query.data?.found ?? 0,
    isFetching: enabled && query.isFetching,
    isIdle: !enabled,
    isUnavailable: query.error instanceof StorefrontSearchUnavailable,
  };
}
